/**
 * Synthase conversation control for the workspace top bar (E5).
 *
 * Renders the active conversation's title — inline editable (rename, persisted)
 * — followed by a chevron that opens a searchable history dropdown, and a
 * new-conversation "+" button. This is the old in-chat conversation header,
 * relocated into the main top bar and split so the title is editable while the
 * chevron (not the title) opens the picker. Styling uses theme tokens (light +
 * dark); the new-chat button and dropdown rows use the framework styled
 * {@link Tooltip}, never a native `title`.
 *
 * @module
 */

import { type JSX, useEffect, useRef, useState } from 'react'

import { useTranslation } from '@molecule/app-react'
import { Tooltip } from '@molecule/app-ui-react'

import { useIsMobile } from '../hooks/useMediaQuery.js'
import { Icon } from './Icon.js'
import { InlineEditableTitle } from './InlineEditableTitle.js'
import { MobileSheet } from './MobileSheet.js'

/** A conversation summary as returned by `GET /projects/:id/conversations`. */
export interface ConversationSummary {
  id: string
  createdAt: string
  updatedAt: string
  /** First user message, truncated — the fallback label when no title is set. */
  preview: string | null
  /** User-supplied title (E5); overrides the preview when present. */
  title?: string | null
}

/** Props for {@link ConversationMenu}. */
export interface ConversationMenuProps {
  conversations: ConversationSummary[]
  /** The active conversation id, or `null` when a fresh chat has no server id yet. */
  activeConversationId: string | null
  /** Switch to an existing conversation. */
  onSelect: (id: string) => void
  /** Start a new conversation. */
  onNewChat: () => void
  /** Persist a rename of the active conversation. */
  onRename: (id: string, title: string) => void | Promise<void>
  /** Called when the dropdown opens, so the host can refresh the list. */
  onOpenDropdown: () => void
  /**
   * Read-only project viewer: switching + searching conversations stays, but
   * the mutating affordances — rename (contentEditable title) and New chat —
   * are hidden (the server 403s those writes below editor).
   */
  readOnly?: boolean
}

/** Human-readable relative time (e.g. "3m ago", "2d ago"). */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (secs < 60) return 'just now'
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

/**
 * Renders the top-bar conversation control.
 *
 * @param props - Component props.
 * @returns The rendered conversation menu.
 */
export function ConversationMenu({
  conversations,
  activeConversationId,
  onSelect,
  onNewChat,
  onRename,
  onOpenDropdown,
  readOnly,
}: ConversationMenuProps): JSX.Element {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [chevronHover, setChevronHover] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  const convList = conversations ?? []
  const active = convList.find((c) => c.id === activeConversationId) ?? null
  const activeLabel = active?.title || active?.preview || ''

  // Close the desktop dropdown on an outside click (the mobile sheet owns its
  // own backdrop dismissal).
  useEffect(() => {
    if (!open || isMobile) return undefined
    const handler = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open, isMobile])

  // Escape closes the desktop dropdown (the sheet binds its own).
  useEffect(() => {
    if (!open || isMobile) return undefined
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setOpen(false)
      }
    }
    // CAPTURE phase, because document-level bubble listeners don't shield each
    // other: when a PanelModal (which binds its Escape handler on the document
    // in the bubble phase) is open underneath this dropdown, both would receive
    // the same keydown and every stacked layer would close together. Capture
    // fires before any bubble listener, and stopPropagation() here keeps the
    // event from ever reaching the modal's handler — so Escape closes only the
    // topmost layer (this dropdown).
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [open, isMobile])

  const toggleDropdown = (): void => {
    setOpen((v) => {
      if (!v) {
        onOpenDropdown()
        setSearch('')
      }
      return !v
    })
  }

  const filtered = search
    ? convList.filter((c) =>
        (c.title || c.preview || '').toLowerCase().includes(search.toLowerCase()),
      )
    : convList

  const newChatLabel = t('workspace.conversation.new', undefined, { defaultValue: 'New chat' })

  return (
    <div
      ref={wrapRef}
      // Flex-fills the space between the project name and the toolbar icons; the
      // conversation title (its `mol-conv-title-fill` child) HUGS its text within
      // this space rather than growing to fill it, and `minWidth:0` here lets the
      // title shrink + ellipsize only when it would overflow (P3-05). The gap
      // matches the left identity group's so user/project/conversation are evenly
      // spaced (P2-24).
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flex: '1 1 auto',
        minWidth: 0,
      }}
    >
      <span style={{ color: 'var(--color-foreground-tertiary)', flexShrink: 0 }}>/</span>

      {/* Active conversation title — contentEditable when there is a persisted
          conversation; otherwise a muted "New chat" placeholder (nothing to
          rename yet). The wrapper hugs its content + the `.mol-conv-title-fill >
          div` rule in Workspace.css makes the Tooltip's inline-block trigger flex
          so the title sizes to its text and its ellipsis has a definite width to
          clip against only when it overflows. */}
      <div
        className="mol-conv-title-fill"
        style={{ display: 'flex', alignItems: 'center', flex: '0 1 auto', minWidth: 0 }}
      >
        {activeConversationId && !readOnly ? (
          <InlineEditableTitle
            value={activeLabel}
            emptyFallback={newChatLabel}
            onSave={(next) => onRename(activeConversationId, next)}
            molId="topbar-conversation-title"
            ariaLabel={t('workspace.conversation.renameAria', undefined, {
              defaultValue: 'Conversation title',
            })}
            editTooltip={t('workspace.conversation.rename', undefined, {
              defaultValue: 'Rename conversation',
            })}
            maxLength={120}
            fill
            // Same fontSize + colour as the user name and project title; only the
            // weight differs — the conversation title is NORMAL weight while the
            // identity (user + project) is semibold (P2-24).
            textStyle={{ fontSize: 13, fontWeight: 400, color: 'var(--color-foreground)' }}
          />
        ) : (
          // Fresh chat (no id) OR read-only viewer: plain, non-editable title.
          <span
            data-mol-id="topbar-conversation-title-empty"
            style={{
              fontSize: 13,
              fontWeight: 400,
              color: 'var(--color-foreground)',
              opacity: 0.6,
              padding: '2px 6px',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {activeConversationId ? activeLabel || newChatLabel : newChatLabel}
          </span>
        )}
      </div>

      {/* The chevron + "+" stay a tight cluster pinned to the right, never
          squeezed (the title flex-fills the space to their left). */}
      <span style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        {/* Dropdown toggle (chevron) — opens the searchable conversation history. */}
        <Tooltip
          content={t('workspace.conversation.history', undefined, {
            defaultValue: 'Conversation history',
          })}
          placement="bottom"
        >
          {/* mol-bespoke-button: icon-only chevron in the workspace TOP BAR, no
              label — a 22px flat glyph that rotates 180° when the menu opens;
              the bar's fixed height has no room for a 26px button box. */}
          <button
            type="button"
            data-mol-id="topbar-conversation-dropdown"
            aria-label={t('workspace.conversation.history', undefined, {
              defaultValue: 'Conversation history',
            })}
            aria-expanded={open}
            aria-haspopup="menu"
            onClick={toggleDropdown}
            onMouseEnter={() => setChevronHover(true)}
            onMouseLeave={() => setChevronHover(false)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              // Phones get a real touch target; desktop keeps the compact 22px.
              width: isMobile ? 38 : 22,
              height: isMobile ? 38 : 22,
              padding: 0,
              border: '1px solid transparent',
              borderRadius: isMobile ? 8 : 5,
              background: chevronHover || open ? 'var(--color-surface)' : 'transparent',
              color: 'var(--mol-color-text-secondary, #aaaaaa)',
              cursor: 'pointer',
              transition: 'background 100ms, transform 120ms',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <Icon name="chevron-down" size={isMobile ? 18 : 14} />
          </button>
        </Tooltip>

        {/* New conversation — hidden for read-only viewers (creating is editor work). */}
        {!readOnly && (
          <Tooltip content={newChatLabel} placement="bottom">
            {/* mol-bespoke-button: icon-only + glyph in the workspace TOP BAR,
                no label — the chevron's 22px twin, sized to the same bar. */}
            <button
              type="button"
              data-mol-id="topbar-conversation-new"
              aria-label={newChatLabel}
              onClick={onNewChat}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: isMobile ? 38 : 22,
                height: isMobile ? 38 : 22,
                padding: 0,
                border: '1px solid transparent',
                borderRadius: isMobile ? 8 : 5,
                background: 'transparent',
                color: 'var(--mol-color-text-secondary, #aaaaaa)',
                cursor: 'pointer',
                transition: 'background 100ms',
              }}
              onMouseEnter={(e) => {
                ;(e.currentTarget as HTMLElement).style.background = 'var(--color-surface)'
              }}
              onMouseLeave={(e) => {
                ;(e.currentTarget as HTMLElement).style.background = 'transparent'
              }}
            >
              <Icon name="plus" size={isMobile ? 18 : 14} />
            </button>
          </Tooltip>
        )}
      </span>

      {/* Conversation history — a bottom sheet on phones (thumb-reachable,
          backdrop-dismissed, never clipped by the viewport edge), the compact
          anchored dropdown on desktop. */}
      {open && isMobile && (
        <MobileSheet
          open
          onClose={() => {
            setOpen(false)
            setSearch('')
          }}
          title={t('workspace.conversation.history', undefined, {
            defaultValue: 'Conversation history',
          })}
          molId="topbar-conversation-sheet"
        >
          <div style={{ padding: '0 16px 10px' }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('workspace.conversation.search', undefined, {
                defaultValue: 'Search conversations…',
              })}
              aria-label={t('workspace.conversation.search', undefined, {
                defaultValue: 'Search conversations…',
              })}
              data-mol-id="topbar-conversation-search"
              style={{
                width: '100%',
                // ≥16px so iOS Safari doesn't zoom the page on focus.
                fontSize: 16,
                padding: '10px 12px',
                background: 'var(--color-surface, #1a1a1a)',
                border: '1px solid var(--color-border)',
                borderRadius: 10,
                outline: 'none',
                color: 'var(--color-foreground)',
              }}
            />
          </div>
          {/* New chat row — hidden for read-only viewers. */}
          {!readOnly && (
            <button
              type="button"
              className="mol-sheet-row"
              data-mol-id="topbar-conversation-sheet-new"
              onClick={() => {
                setOpen(false)
                setSearch('')
                onNewChat()
              }}
            >
              <span className="mol-sheet-row-icon">
                <Icon name="plus" size={20} />
              </span>
              <span className="mol-sheet-row-label">{newChatLabel}</span>
            </button>
          )}
          <div className="mol-sheet-divider" aria-hidden="true" />
          {filtered.length === 0 ? (
            <div
              style={{
                padding: '16px',
                fontSize: 14,
                color: 'var(--mol-color-text-secondary, #888)',
              }}
            >
              {t('workspace.conversation.empty', undefined, {
                defaultValue: 'No conversations yet',
              })}
            </div>
          ) : (
            filtered.map((conv) => (
              // mol-bespoke-button: a mobile-sheet LIST ROW, not a CTA — the
              // `.mol-sheet-row` chrome owns its look; the only inline value is
              // the token-mixed selected-row wash.
              <button
                key={conv.id}
                type="button"
                className="mol-sheet-row"
                data-mol-id={`topbar-conversation-item-${conv.id}`}
                onClick={() => {
                  onSelect(conv.id)
                  setOpen(false)
                  setSearch('')
                }}
                style={
                  conv.id === activeConversationId
                    ? {
                        background:
                          'color-mix(in srgb, var(--mol-color-primary, #6366f1) 14%, transparent)',
                      }
                    : undefined
                }
              >
                <span className="mol-sheet-row-label">
                  {conv.title ||
                    conv.preview ||
                    t('workspace.conversation.untitled', undefined, {
                      defaultValue: 'New conversation',
                    })}
                </span>
                <span className="mol-sheet-row-meta">{relativeTime(conv.updatedAt)}</span>
              </button>
            ))
          )}
        </MobileSheet>
      )}

      {/* Searchable history dropdown (desktop). */}
      {open && !isMobile && (
        <div
          role="menu"
          data-mol-id="topbar-conversation-list"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 6,
            minWidth: 280,
            maxWidth: 360,
            maxHeight: 360,
            overflowY: 'auto',
            zIndex: 60,
            background: 'var(--color-surface, #1a1a1a)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            boxShadow: '0 8px 28px rgba(0, 0, 0, 0.35)',
          }}
        >
          <div
            style={{
              position: 'sticky',
              top: 0,
              padding: '8px 10px',
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-surface, #1a1a1a)',
            }}
          >
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('workspace.conversation.search', undefined, {
                defaultValue: 'Search conversations…',
              })}
              aria-label={t('workspace.conversation.search', undefined, {
                defaultValue: 'Search conversations…',
              })}
              data-mol-id="topbar-conversation-search"
              autoFocus
              style={{
                width: '100%',
                fontSize: 13,
                padding: '4px 6px',
                background: 'transparent',
                border: '1px solid var(--color-border)',
                borderRadius: 5,
                outline: 'none',
                color: 'var(--color-foreground)',
              }}
            />
          </div>

          {filtered.length === 0 ? (
            <div
              style={{
                padding: '12px',
                fontSize: 12,
                color: 'var(--mol-color-text-secondary, #888)',
              }}
            >
              {t('workspace.conversation.empty', undefined, {
                defaultValue: 'No conversations yet',
              })}
            </div>
          ) : (
            filtered.map((conv) => (
              // mol-bespoke-button: a dropdown MENU ITEM (role="menuitem"), not
              // a CTA — two stacked text lines filling the menu's width; a
              // button box per row would turn the menu into a button list.
              <button
                key={conv.id}
                type="button"
                role="menuitem"
                data-mol-id={`topbar-conversation-item-${conv.id}`}
                onClick={() => {
                  onSelect(conv.id)
                  setOpen(false)
                  setSearch('')
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 2,
                  width: '100%',
                  padding: '8px 12px',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: 'var(--color-foreground)',
                  background:
                    conv.id === activeConversationId
                      ? 'color-mix(in srgb, var(--mol-color-primary, #6366f1) 14%, transparent)'
                      : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (conv.id !== activeConversationId)
                    (e.currentTarget as HTMLElement).style.background =
                      'var(--color-surface-hover, rgba(128,128,128,0.12))'
                }}
                onMouseLeave={(e) => {
                  if (conv.id !== activeConversationId)
                    (e.currentTarget as HTMLElement).style.background = 'transparent'
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}
                >
                  {conv.title ||
                    conv.preview ||
                    t('workspace.conversation.untitled', undefined, {
                      defaultValue: 'New conversation',
                    })}
                </span>
                <span style={{ fontSize: 11, color: 'var(--mol-color-text-secondary, #888)' }}>
                  {relativeTime(conv.updatedAt)}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
